/*
 * The promotion rail: dev -> stage -> production. Only the stages a repo
 * actually has are drawn, so a single-branch repo shows one card rather than
 * two placeholders, and the mapping comes from the server (branch names never
 * appear in this file).
 *
 * Each connector carries what is waiting to move across it. The rail only
 * animates where something is actually pending, so the motion means "there is
 * flow here" instead of being decoration.
 *
 * Each card links out to where that environment is actually deployed, when
 * the repo's .github/branch-viz.json says where that is, and reports whether
 * that URL is currently answering. A failing environment states the failure on
 * the card rather than hiding it in a tooltip: the whole reason to draw a rail
 * is to see at a glance which end of it is broken.
 */
import { CaretRight, Warning } from './Icons'
import { Avatar, Checks } from './Bits'
import { LiveBadge } from './LiveBadge'
import { rel } from '../lib/format'
import { isFailing } from '../lib/diagnostics'
import type { Branch, EnvironmentHealth, EnvKey, Promotion, RepoState } from '../types'

const ORDER: EnvKey[] = ['dev', 'stage', 'production']
const MIDDOT = '·'

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

function Connector({ promotion, animate }: { promotion: Promotion | null; animate: boolean }) {
  if (!promotion || promotion.ahead === null) {
    return (
      <div className="connector" title={promotion ? 'GitHub could not compare these branches' : undefined}>
        {promotion ? <div className="promo unknown">not comparable</div> : null}
        <div className="railrow">
          <div className="rail static" />
          <CaretRight size={13} />
        </div>
      </div>
    )
  }

  const { ahead, behind, sourceBranch, targetBranch } = promotion
  const waiting = ahead > 0
  const detail = [
    waiting
      ? `${plural(ahead, 'commit')} on ${sourceBranch} not yet in ${targetBranch}`
      : `${sourceBranch} is fully merged into ${targetBranch}`,
    behind ? `${targetBranch} has ${plural(behind, 'commit')} that ${sourceBranch} does not` : null
  ]
    .filter(Boolean)
    .join(` ${MIDDOT} `)

  return (
    <div className="connector" title={detail}>
      <div className={`promo ${waiting ? 'waiting' : 'synced'}`}>
        {waiting ? `${ahead} to promote` : 'in sync'}
      </div>
      <div className="railrow">
        <div className={`rail ${animate && waiting ? 'animate' : 'static'}`} />
        <CaretRight size={13} />
      </div>
      {behind ? <div className="promo behind">{behind} behind</div> : null}
    </div>
  )
}

export function Pipeline({
  state,
  animate,
  health,
  checking
}: {
  state: RepoState
  animate: boolean
  health?: EnvironmentHealth[]
  checking?: boolean
}) {
  const healthByEnv = new Map((health ?? []).map(h => [h.env, h]))
  const byEnv = new Map<EnvKey, Branch>()
  for (const b of state.branches) if (b.env) byEnv.set(b.env, b)
  const stages = ORDER.filter(env => byEnv.has(env))
  const promotionTo = new Map(state.promotions.map(p => [p.to, p]))

  if (stages.length === 0) return null

  return (
    <section className="section">
      <div className="section-label">
        Deployment pipeline
        {stages.length === 1 ? <span className="count">{MIDDOT} single branch, no promotion rail</span> : null}
      </div>
      <div className="pipeline">
        {stages.map((env, i) => {
          const branch = byEnv.get(env)!
          const last = branch.commits[0]
          const liveUrl = state.environmentUrls[env]
          const envHealth = healthByEnv.get(env)
          return (
            <div key={env} style={{ display: 'contents' }}>
              {i > 0 ? <Connector promotion={promotionTo.get(env) ?? null} animate={animate} /> : null}
              <div className="card envcard">
                <div className="envcard-top">
                  <span className={`tag ${env === 'production' ? 'tag-accent' : 'tag-neutral'}`}>{env}</span>
                  <div className="envcard-right">
                    <span className="envcard-role">{branch.role}</span>
                    {liveUrl ? <LiveBadge url={liveUrl} health={envHealth} checking={checking} /> : null}
                  </div>
                </div>
                <div className="envcard-branch">
                  <span>{branch.name}</span>
                  <Checks state={branch.checks} />
                </div>
                <div className="envcard-msg" title={last?.message}>{last?.message ?? 'No commits'}</div>
                {last ? (
                  <div className="envcard-foot">
                    <Avatar person={last.author} />
                    <span className="alias">{last.author.alias}</span>
                    <span className="rel">{rel(last.committedAt)}</span>
                  </div>
                ) : null}
                {envHealth && isFailing(envHealth) ? (
                  <div className="envcard-error" title={envHealth.body ?? undefined}>
                    <Warning size={12} />
                    <span>{envHealth.error ?? 'unreachable'}</span>
                  </div>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
